import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import ServiceSelect from './ServiceSelect';
import DateTimeSelect from './DateTimeSelect';
import ClientInfoStep from './ClientInfoStep';
import PaymentStep from './PaymentStep';
import ReviewStep from './ReviewStep';
import api from '../../api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const ACCENT = '#2B7FFF';
const STEPS = ['Service', 'Date & Time', 'Your Info', 'Hold Spot', 'Confirm'];

const FALLBACK_SERVICES = [
  { id: 1, name: 'Flair Classic', description: 'Classic precision cut with scissor or clipper finish', duration_minutes: 45, price: '45.00', after_hours_price: '75.00', is_mobile: false, request_only: false, badge: 'MOST POPULAR' },
  { id: 2, name: 'Flair Classic & Beard', description: 'Full haircut combo with beard trim and lineup', duration_minutes: 60, price: '55.00', after_hours_price: '85.00', is_mobile: false, request_only: false, badge: 'BEST VALUE' },
  { id: 3, name: 'Flair Deluxe Package', description: 'The full luxury experience — cut, beard, hot towel & more', duration_minutes: 90, price: '100.00', after_hours_price: '150.00', is_mobile: false, request_only: false, badge: 'PREMIUM' },
  { id: 4, name: 'Deep Facial', description: 'Deep cleansing facial treatment for smooth, refreshed skin', duration_minutes: 20, price: '60.00', after_hours_price: null, is_mobile: false, request_only: false, badge: 'QUICK ADD-ON' },
  { id: 5, name: 'Kids (5–11yo)', description: 'Precision cut for children ages 5 to 11', duration_minutes: 40, price: '35.00', after_hours_price: null, is_mobile: false, request_only: false, badge: null },
  { id: 6, name: 'Flair Express (Mobile)', description: 'We come to you — perfect for events & gatherings', duration_minutes: 180, price: '150.00', after_hours_price: null, is_mobile: true, request_only: true, badge: 'VIP MOBILE' },
  { id: 7, name: 'Color', description: 'Professional hair coloring service', duration_minutes: 90, price: '100.00', after_hours_price: null, is_mobile: false, request_only: false, badge: null },
];

function DeepFacialUpsell({ service, deepFacialService, onAccept, onDecline }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
    }}>
      <div style={{
        background: '#0F1929', border: '1px solid rgba(43,127,255,0.35)', borderRadius: '14px',
        padding: '36px 28px', maxWidth: '440px', width: '100%', textAlign: 'center',
        animation: 'card-stagger 0.25s ease forwards',
      }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '50%',
          background: 'rgba(43,127,255,0.1)', border: '1.5px solid rgba(43,127,255,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px', fontSize: '24px',
        }}>✦</div>

        <div style={{ color: ACCENT, fontSize: '10px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '10px' }}>
          Limited Add-On
        </div>
        <h2 style={{ fontSize: '22px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '-0.01em', marginBottom: '10px' }}>
          Add a Deep Facial?
        </h2>
        <p style={{ color: '#888', fontSize: '14px', lineHeight: 1.7, marginBottom: '24px' }}>
          Upgrade your {service.name} with a <strong style={{ color: '#fff' }}>Deep Facial treatment</strong> —
          only <strong style={{ color: ACCENT }}>20 mins</strong> and just <strong style={{ color: ACCENT }}>$60 more</strong>.
          Deep cleanse, refresh, and leave looking sharp.
        </p>

        <div style={{
          background: 'rgba(5,8,16,0.85)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '14px 18px',
          marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 700, fontSize: '14px' }}>Deep Facial</div>
            <div style={{ color: '#555', fontSize: '12px', marginTop: '2px' }}>20 min · Add-on treatment</div>
          </div>
          <div style={{ color: ACCENT, fontWeight: 900, fontSize: '20px' }}>+$60</div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={onAccept}
            className="btn btn-primary"
            style={{ flex: 1, padding: '14px', fontSize: '14px', fontWeight: 800 }}
          >
            Yes, Add It!
          </button>
          <button
            onClick={onDecline}
            className="btn btn-ghost"
            style={{ flex: 1, padding: '14px', fontSize: '14px', color: '#555', border: '1px solid #333' }}
          >
            No Thanks
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BookingFlow() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const preselectedServiceId = searchParams.get('service');
  const refCode = searchParams.get('ref') || '';

  const [step, setStep] = useState(0);
  const [service, setService] = useState(null);
  const [slot, setSlot] = useState(null);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({ name: '', phone: '', secondDate: '' });
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [clientInfo, setClientInfo] = useState(null);
  const [client, setClient] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [services, setServices] = useState([]);
  const [showUpsell, setShowUpsell] = useState(false);
  const [addDeepFacial, setAddDeepFacial] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/services')
      .then(r => {
        setServices(r.data);
        // Auto-select service if ?service=ID in URL
        if (preselectedServiceId) {
          const match = r.data.find(s => String(s.id) === String(preselectedServiceId));
          if (match) { setService(match); setStep(1); }
        }
      })
      .catch(() => {
        const fallback = FALLBACK_SERVICES;
        setServices(fallback);
        if (preselectedServiceId) {
          const match = fallback.find(s => String(s.id) === String(preselectedServiceId));
          if (match) { setService(match); setStep(1); }
        }
      });
  }, [preselectedServiceId]);

  const deepFacialService = services.find(s => s.name === 'Deep Facial');

  const handleServiceSelect = (svc) => {
    setService(svc);
    setAddDeepFacial(false);
    if (deepFacialService && svc.id !== deepFacialService.id) {
      setShowUpsell(true);
    } else {
      setStep(1);
    }
  };

  const handleUpsellAccept = () => { setAddDeepFacial(true); setShowUpsell(false); setStep(1); };
  const handleUpsellDecline = () => { setAddDeepFacial(false); setShowUpsell(false); setStep(1); };
  const handleSlotSelect = (s) => { setSlot(s); setStep(2); };

  // Step 2→3: collect client info, create client + setup intent, then show card step
  const handleClientInfo = async (info) => {
    setClientInfo(info);
    setLoading(true);
    setError('');
    try {
      const { data: newClient } = await api.post('/clients', {
        name: info.name,
        phone: info.phone,
        email: info.email,
      });
      setClient(newClient);

      const { data: setupData } = await api.post('/payments/setup-intent', {
        client_id: newClient.id,
      });
      setStripeClientSecret(setupData.clientSecret);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3→4: card confirmed — save payment method and go to review
  const handlePaymentComplete = async (paymentMethodId) => {
    try {
      await api.post('/payments/confirm-card', {
        client_id: client.id,
        payment_method_id: paymentMethodId,
      });
      setStep(4);
    } catch (err) {
      setError('Failed to save card. Please try again.');
    }
  };

  // Step 4: submit booking (client already created)
  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: appt } = await api.post('/appointments', {
        client_id: client.id,
        service_id: service.id,
        appointment_date: slot.date,
        start_time: slot.start_time,
        notes: clientInfo.notes,
        mobile_location: clientInfo.mobile_location || null,
        mobile_event_type: clientInfo.mobile_event_type || null,
        mobile_people_count: clientInfo.mobile_people_count ? parseInt(clientInfo.mobile_people_count) : null,
        referral_code: refCode || undefined,
      });

      let deepFacialAppt = null;
      if (addDeepFacial && deepFacialService) {
        try {
          const [sh, sm] = slot.start_time.split(':').map(Number);
          const dfStartMinutes = sh * 60 + sm + parseInt(service.duration_minutes, 10);
          const dfH = Math.floor(dfStartMinutes / 60);
          const dfM = dfStartMinutes % 60;
          const dfStartTime = `${String(dfH).padStart(2,'0')}:${String(dfM).padStart(2,'0')}`;

          const { data: dfAppt } = await api.post('/appointments', {
            client_id: client.id,
            service_id: deepFacialService.id,
            appointment_date: slot.date,
            start_time: dfStartTime,
            notes: `Add-on for appointment #${appt.id}`,
          });
          deepFacialAppt = dfAppt;
        } catch (e) {
          console.warn('Deep Facial add-on booking failed:', e.message);
        }
      }

      navigate('/booking/confirmation', {
        state: { appointment: appt, service, slot, client, deepFacialAppt, deepFacialService: addDeepFacial ? deepFacialService : null },
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const progressPct = (step / (STEPS.length - 1)) * 100;

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setWaitlistLoading(true);
    try {
      await api.post('/waitlist', {
        client_name: waitlistForm.name || clientInfo?.name || '',
        phone: waitlistForm.phone || clientInfo?.phone || '',
        service_id: service?.id || null,
        preferred_date: slot?.date || '',
        second_preferred_date: waitlistForm.secondDate || null,
      });
      setWaitlistSubmitted(true);
    } catch (err) {
      console.error('Waitlist error:', err);
    } finally {
      setWaitlistLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '48px 24px', background: 'rgba(5,8,16,0.82)', borderRadius: '18px', boxShadow: '0 0 80px rgba(0,0,0,0.6)' }}>
      {showUpsell && deepFacialService && (
        <DeepFacialUpsell
          service={service}
          deepFacialService={deepFacialService}
          onAccept={handleUpsellAccept}
          onDecline={handleUpsellDecline}
        />
      )}

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '36px' }}>
        <div style={{ color: '#2B7FFF', fontSize: '11px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '10px' }}>
          MANE
        </div>
        <h1 className="page-title">Request Appointment</h1>
      </div>

      {/* Progress bar */}
      <div className="booking-progress">
        <div className="booking-progress-steps">
          {STEPS.map((label, i) => (
            <span
              key={i}
              className={`booking-progress-step${i === step ? ' active' : i < step ? ' done' : ''}`}
            >
              {label}
            </span>
          ))}
        </div>
        <div className="booking-progress-track">
          <div className="booking-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Deep Facial add-on indicator */}
      {addDeepFacial && step > 0 && (
        <div style={{
          background: 'rgba(43,127,255,0.06)', border: '1px solid rgba(43,127,255,0.25)',
          borderRadius: '6px', padding: '10px 16px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
        }}>
          <span style={{ color: ACCENT, fontSize: '13px', fontWeight: 600 }}>
            Deep Facial add-on included (+$60)
          </span>
          <button
            onClick={() => setAddDeepFacial(false)}
            style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '13px' }}
          >
            Remove
          </button>
        </div>
      )}

      {/* Global error (e.g. during client creation / setup intent) */}
      {error && step !== 3 && (
        <div style={{
          color: '#ef4444', background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.25)', padding: '12px 16px', borderRadius: '6px',
          fontSize: '14px', marginBottom: '16px',
        }}>
          {error}
        </div>
      )}

      {/* Steps */}
      <div key={step} className="booking-step-enter">
        {step === 0 && <ServiceSelect onSelect={handleServiceSelect} services={services} />}
        {step === 1 && (
          <>
            <DateTimeSelect
              service={service}
              onSelect={handleSlotSelect}
              onBack={() => setStep(0)}
            />
            {/* Waitlist CTA */}
            {!showWaitlist ? (
              <div style={{ marginTop: 20, textAlign: 'center' }}>
                <button
                  onClick={() => { setShowWaitlist(true); setWaitlistForm({ name: clientInfo?.name || '', phone: clientInfo?.phone || '', secondDate: '' }); }}
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#6b7280', fontSize: 13, padding: '10px 20px', cursor: 'pointer' }}
                >
                  Can't find a time that works? → Join Waitlist
                </button>
              </div>
            ) : waitlistSubmitted ? (
              <div style={{ marginTop: 20, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 16, padding: 24, textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>✓</div>
                <div style={{ color: '#10b981', fontWeight: 800, fontSize: 16, marginBottom: 8 }}>You're on the list!</div>
                <div style={{ color: '#6b7280', fontSize: 14 }}>We'll text you when a spot opens up.</div>
              </div>
            ) : (
              <div style={{ marginTop: 20, background: '#0d1117', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: 24 }}>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#fff', marginBottom: 4 }}>Join the Waitlist</div>
                <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 20 }}>We'll reach out the moment a spot opens up.</div>
                <form onSubmit={handleWaitlistSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <div style={{ color: '#6b7280', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Your Name *</div>
                    <input
                      required
                      value={waitlistForm.name}
                      onChange={e => setWaitlistForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="John Smith"
                      style={{ width: '100%', background: '#0a0f1e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 12, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <div style={{ color: '#6b7280', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Phone *</div>
                    <input
                      required
                      value={waitlistForm.phone}
                      onChange={e => setWaitlistForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="(561) 555-0100"
                      style={{ width: '100%', background: '#0a0f1e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 12, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  {slot?.date && (
                    <div style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#D4AF37' }}>
                      Preferred date: {slot.date}
                    </div>
                  )}
                  <div>
                    <div style={{ color: '#6b7280', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Any flexibility on date? (optional)</div>
                    <input
                      type="date"
                      value={waitlistForm.secondDate}
                      onChange={e => setWaitlistForm(f => ({ ...f, secondDate: e.target.value }))}
                      style={{ width: '100%', background: '#0a0f1e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 12, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      type="submit"
                      disabled={waitlistLoading}
                      style={{ flex: 1, background: 'linear-gradient(135deg, #D4AF37, #f5c842)', color: '#000', border: 'none', borderRadius: 10, padding: '14px', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}
                    >
                      {waitlistLoading ? 'Submitting…' : "Join Waitlist"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowWaitlist(false)}
                      style={{ padding: '14px 16px', background: 'transparent', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, color: '#6b7280', fontSize: 13, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
        {step === 2 && (
          <ClientInfoStep
            service={service}
            slot={slot}
            onSubmit={handleClientInfo}
            onBack={() => setStep(1)}
            loading={loading}
          />
        )}
        {step === 3 && stripeClientSecret && (
          <Elements stripe={stripePromise} options={{
            clientSecret: stripeClientSecret,
            fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap' }],
            appearance: { theme: 'night', variables: { colorPrimary: '#2B7FFF', fontFamily: 'Inter, sans-serif' } },
          }}>
            <PaymentStep
              clientSecret={stripeClientSecret}
              onComplete={handlePaymentComplete}
              onBack={() => setStep(2)}
            />
          </Elements>
        )}
        {step === 4 && (
          <ReviewStep
            service={service}
            slot={slot}
            clientInfo={clientInfo}
            addDeepFacial={addDeepFacial}
            deepFacialService={deepFacialService}
            onSubmit={handleSubmit}
            onBack={() => setStep(3)}
            loading={loading}
            error={error}
          />
        )}
      </div>
    </div>
  );
}
